import { useSuspenseQuery } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { makeMaxHostsQueryOptions } from '#api/hosts.queries.ts';
import { Code } from '#components/atoms/code/index.ts';
import { Input, Label } from '#components/atoms/forms/index.ts';
import { Pill } from '#components/atoms/pill/Pill.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { STRATEGIES, type Strategy, type StrategyID } from '../canonicalize.ts';
import styles from './CanonicalizationPreviewPage.module.css';

type Row = {
  /** Host names can repeat within an org, so host rows key on the host id. */
  key: string;
  name: string;
  source: 'host' | 'demo' | 'tried';
};

type CellResult = {
  strategyId: StrategyID;
  label: string | null;
  duplicate: boolean;
};

/** Names picked to exercise the cases the strategies disagree on. */
const DEMO_NAMES = [
  'Alice’s MacBook Pro',
  'café-au-lait',
  'тест-машина',
  '\u{1f680} rocket box',
  'build.internal.example',
];

export function CanonicalizationPreviewPage() {
  const {
    data: { data: hosts },
  } = useSuspenseQuery(makeMaxHostsQueryOptions());
  const [triedName, setTriedName] = useState('');
  const inputId = useId();

  // Canonicalizing the whole host list is the expensive part (an IDNA
  // round-trip per host for fold-punycode), so it only recomputes when the
  // host list changes; the tried row recomputes alone on each keystroke.
  const { rows, columns } = useMemo(() => {
    const hostRows: Row[] = [
      ...hosts.map((host) => ({ key: `host-${host.id}`, name: host.name, source: 'host' as const })),
      ...DEMO_NAMES.map((name) => ({ key: `demo-${name}`, name, source: 'demo' as const })),
    ];
    return { rows: hostRows, columns: STRATEGIES.map((strategy) => computeColumn(strategy, hostRows)) };
  }, [hosts]);

  const tableRows = [
    ...rows.map((row, rowIndex) => ({
      ...row,
      results: columns.map(
        (column): CellResult => ({
          strategyId: column.strategy.id,
          label: column.labels[rowIndex] ?? null,
          duplicate: column.duplicates[rowIndex] ?? false,
        })
      ),
    })),
    ...(triedName.trim() !== '' ? [makeTriedRow(triedName, columns)] : []),
  ];

  return (
    <MainLayout header="DNS name preview">
      <p className={`p-md mb-24 ${styles.CanonicalizationPreviewPage_intro}`}>
        How this network’s host names would canonicalize into DNS labels under each candidate strategy. Names that
        collide, come back empty, or get rejected are flagged.
      </p>
      <div className={styles.CanonicalizationPreviewPage_cards}>
        {columns.map(({ strategy, tallies }) => (
          <article className={styles.CanonicalizationPreviewPage_card} key={strategy.id}>
            <h2 className="h5">{strategy.label}</h2>
            <p className={`p-sm ${styles.CanonicalizationPreviewPage_description}`}>{strategy.description}</p>
            <dl className={styles.CanonicalizationPreviewPage_tallies}>
              <div>
                <dt>Changed</dt>
                <dd>{tallies.changed}</dd>
              </div>
              <div>
                <dt>Colliding</dt>
                <dd>{tallies.collisions}</dd>
              </div>
              <div>
                <dt>Rejected</dt>
                <dd>{tallies.rejected}</dd>
              </div>
              <div>
                <dt>Empty</dt>
                <dd>{tallies.empty}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
      <div className={styles.CanonicalizationPreviewPage_tryName}>
        <Label htmlFor={inputId}>Try a name</Label>
        <Input
          id={inputId}
          value={triedName}
          onChange={(event) => setTriedName(event.target.value)}
          placeholder="Rachel’s PC (upstairs)"
        />
      </div>
      <div className={styles.CanonicalizationPreviewPage_tableWrap}>
        <table className={styles.CanonicalizationPreviewPage_table}>
          <thead>
            <tr>
              <th scope="col">Host name</th>
              {STRATEGIES.map((strategy) => (
                <th scope="col" key={strategy.id}>
                  {strategy.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row) => (
              <tr
                key={row.key}
                className={row.source === 'tried' ? styles.CanonicalizationPreviewPage_triedRow : undefined}
              >
                <th scope="row">
                  <span className={styles.CanonicalizationPreviewPage_name}>
                    {row.name}
                    {row.source === 'demo' ?
                      <Pill>demo</Pill>
                    : null}
                  </span>
                </th>
                {row.results.map((result) => (
                  <td key={result.strategyId}>
                    <ResultCell result={result} original={row.name} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
}

type Column = {
  strategy: Strategy;
  labels: Array<string | null>;
  duplicates: boolean[];
  /** Canonical labels claimed by real hosts, for collision checks against a tried name. */
  hostCounts: Map<string, number>;
  tallies: { changed: number; collisions: number; rejected: number; empty: number };
};

/**
 * Duplicate flags and tallies describe only the real host list — a synthetic
 * demo name landing on a host's label must not flag the host's row, and the
 * tally cards promise the state of this network, not of the demo rows.
 */
function computeColumn(strategy: Strategy, rows: Row[]): Column {
  const labels = rows.map((row) => strategy.canonicalize(row.name));
  const hostCounts = new Map<string, number>();
  labels.forEach((label, index) => {
    if (rows[index]?.source === 'host' && label !== null && label !== '') {
      hostCounts.set(label, (hostCounts.get(label) ?? 0) + 1);
    }
  });
  const duplicates = labels.map((label, index) =>
    rows[index]?.source === 'host' && label !== null && label !== '' ? (hostCounts.get(label) ?? 0) > 1 : false
  );
  const hostLabels = labels.filter((_, index) => rows[index]?.source === 'host');
  return {
    strategy,
    labels,
    duplicates,
    hostCounts,
    tallies: {
      changed: rows.filter((row, index) => {
        const label = labels[index];
        return row.source === 'host' && label !== null && label !== '' && label !== row.name;
      }).length,
      collisions: duplicates.filter(Boolean).length,
      rejected: hostLabels.filter((label) => label === null).length,
      empty: hostLabels.filter((label) => label === '').length,
    },
  };
}

/** A tried name is flagged as duplicate when it lands on a label a real host already claims. */
function makeTriedRow(name: string, columns: Column[]) {
  return {
    key: 'tried',
    name,
    source: 'tried' as const,
    results: columns.map((column): CellResult => {
      const label = column.strategy.canonicalize(name);
      return {
        strategyId: column.strategy.id,
        label,
        duplicate: label !== null && label !== '' && (column.hostCounts.get(label) ?? 0) > 0,
      };
    }),
  };
}

function ResultCell({ result, original }: { result: CellResult; original: string }) {
  if (result.label === null) {
    return <Pill variant="alert">rejected</Pill>;
  }
  if (result.label === '') {
    return <Pill variant="alert">empty</Pill>;
  }
  return (
    <span className={styles.CanonicalizationPreviewPage_result}>
      <Code className={result.label === original ? styles.CanonicalizationPreviewPage_unchanged : undefined}>
        {result.label}
      </Code>
      {result.duplicate ?
        <Pill variant="alert">duplicate</Pill>
      : null}
    </span>
  );
}
